import { useMemo, useCallback } from 'react';
import {
  Button,
  Col,
  Row,
  Card,
  ButtonToolbar,
  ButtonGroup,
  Form,
} from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useNavigate, useParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import setFieldTouched from 'final-form-set-field-touched';
import { v4 as uuidv4 } from 'uuid';

import get from 'lodash.get';
import cloneDeep from 'lodash.clonedeep';

import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import { settingsSet } from '../store/settings_slice';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderError, renderOffline } from '../components/render_helpers';
import SubmitButtonSpinner from '../components/submit_button_spinner';
import Field from '../components/form/rff_field';
import InputField from '../components/form/input_field';
import { coerceInput, pickValues, handleSubmitError } from '../lib/utils';

import {
  consignmentImageFormPageQuery,
  consignmentImageCreate as consignmentImageCreateMutation,
  consignmentImageUpdate as consignmentImageUpdateMutation,
} from '../graphql/consignment_image_queries';
import * as updateFunctions from '../update_functions';
import { consignmentImageDefaultValues } from '../defaults';
import { consignmentImageFormValidator } from '../validators';
import { consignmentImageWhiteList } from '../white_lists';

const ConsignmentImageForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const [consignmentImageCreate] = useMutation(consignmentImageCreateMutation);
  const [consignmentImageUpdate] = useMutation(consignmentImageUpdateMutation);
  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    networkStatus: pageNetworkStatus,
  } = useQuery(consignmentImageFormPageQuery, {
    variables: {
      hasConsignmentImageId: !!params.id,
      consignmentImageId: params.id || 0,
    },
    notifyOnNetworkStatusChange: true,
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const initialValues = useMemo(() => {
    if (pageData?.consignmentImage) {
      const values = pickValues(pageData.consignmentImage, consignmentImageWhiteList);
      return values;
    }
    return consignmentImageDefaultValues;
  }, [pageData]);

  const handleFileChange = useCallback((name, onChange, e) => {
    const file = get(e, 'target.files.0');
    if (file) {
      onChange(file);
    }
  }, []);

  const onCancel = () => {
    navigate('/consignment_images');
  };

  const onFormSubmit = async (data) => {
    const uuid = uuidv4();
    const submitData = cloneDeep(data);
    let mutation;
    let mutationMessageAction;
    const input = coerceInput(submitData);
    const mutationData = {
      variables: { input },
      context: {
        hasUpload: true,
        serializationKey: `${settingsTenant}-images`,
        tracked: true,
        recordType: 'ConsignmentImageType',
        recordId: params.id || uuid,
      },
    };
    if (params.id) {
      mutation = consignmentImageUpdate;
      mutationMessageAction = 'update';
      mutationData.variables.id = params.id;
      mutationData.context.mutationType = 'UPDATE';
      mutationData.update = updateFunctions.consignmentImageUpdate;
      mutationData.optimisticResponse = updateFunctions.optimistic(
        'consignmentImageUpdate',
        mutationData
      );
    } else {
      mutation = consignmentImageCreate;
      mutationData.context.mutationType = 'CREATE';
      mutationMessageAction = 'create';
      mutationData.update = updateFunctions.consignmentImageCreate;
      mutationData.optimisticResponse = updateFunctions.optimistic(
        'consignmentImageCreate',
        mutationData
      );
    }
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await mutation(mutationData);
        toastSuccess(`Consignment Image ${mutationMessageAction} succeeded`);
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
        navigate('/consignment_images');
      } catch (err) {
        const { errorMessage, submitErrors } = handleSubmitError(err);
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
        toastError(errorMessage);
        return submitErrors;
      }
    } else {
      mutation(mutationData);
      toastWarning(
        `Consignment Image ${mutationMessageAction} ok locally. Go online to make permanent`
      );
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
      navigate('/consignment_images');
    }
    return undefined;
  };

  // id: Int!
  // consignmentId: Int!
  // consignmentItemId: Int
  // fileFilename: String
  // fileMimetype: String
  // filePathnameOriginal: String
  // filePathnameOptimized: String
  // signedUrlOriginal: String
  // signedUrlOptimized: String
  // consignment: ConsignmentType
  // consignmentItem: ConsignmentItemType

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">
                {params.id ? 'Edit Consignment Image' : 'New Consignment Image'}
              </h1>
            </div>
            <div className="float-end">
              <LinkContainer to="/consignment_images">
                <Button className="me-2" variant="primary">
                  All Consignment Images
                </Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <FinalForm
            initialValues={initialValues}
            onSubmit={(data) => onFormSubmit(data)}
            validate={consignmentImageFormValidator}
            mutators={{ setFieldTouched }}
          >
            {({ handleSubmit, pristine, submitting }) => (
              <form noValidate>
                <Card>
                  <Card.Body>
                    <Field
                      name="consignmentId"
                      component={InputField}
                      asElement="select"
                      selectOptions={get(pageData, 'consignmentList', []).map(
                        ({ id, supplier: { name: supplierName }, name }) => ({
                          id,
                          name: [supplierName, name].join('-'),
                        })
                      )}
                      defaultSelectOptionText="select consignment..."
                      labelWidth={4}
                      inputWidth={4}
                      size="lg"
                    >
                      Consignment
                    </Field>
                    <Field
                      type="file"
                      name="file"
                      labelWidth={4}
                      inputWidth={4}
                      size="lg"
                      component={InputField}
                      customOnChange={handleFileChange}
                    />
                    <Form.Group as={Row}>
                      <Col sm={12}>
                        <ButtonToolbar style={{ justifyContent: 'flex-end' }}>
                          <ButtonGroup className="me-2">
                            <Button
                              variant="danger"
                              onClick={onCancel}
                              disabled={submitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="primary"
                              disabled={pristine || submitting}
                              onClick={handleSubmit}
                            >
                              {submitting && <SubmitButtonSpinner />}
                              {params.id ? 'Update' : 'Create'}
                            </Button>
                          </ButtonGroup>
                        </ButtonToolbar>
                      </Col>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </form>
            )}
          </FinalForm>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default ConsignmentImageForm;
